"use client";

import Link from "next/link";
import { Home, ArrowRight, Activity, Search } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useRef } from "react";

export default function NotFound() {
    const containerRef = useRef<HTMLDivElement>(null);
    const floatRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        // Entrance Animation
        const tl = gsap.timeline();
        tl.fromTo(contentRef.current,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
        )
            .fromTo(statsRef.current,
                { opacity: 0, y: 10 },
                { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
                "-=0.2"
            );

        // Floating/Pulse Animation for the blob
        if (floatRef.current) {
            gsap.to(floatRef.current, {
                rotation: 10,
                scale: 1.1,
                duration: 5,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut"
            });
        }
    }, { scope: containerRef });

    return (
        <div ref={containerRef} className="min-h-[80vh] flex items-center justify-center bg-gray-50 relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-blue-100/50 rounded-full blur-[100px]" />
                <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] bg-primary/5 rounded-full blur-[80px]" />
                <div className="absolute bottom-[10%] left-[20%] w-[20%] h-[20%] bg-blue-50 rounded-full blur-[60px]" />
            </div>

            <div className="container mx-auto px-4 z-10 text-center">
                <div ref={contentRef} className="opacity-0">
                    {/* 404 Text */}
                    <div className="relative inline-block mb-4">
                        <h1 className="text-[120px] md:text-[180px] font-bold text-[#001746] leading-none font-serif tracking-tighter">
                            404
                        </h1>
                        <div
                            ref={floatRef}
                            className="absolute top-[20%] right-[15%] w-16 h-16 md:w-24 md:h-24 bg-primary rounded-full blur-2xl opacity-20 mix-blend-multiply"
                        />
                    </div>

                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                        Page Not Found
                    </h2>

                    <p className="text-lg text-gray-600 max-w-lg mx-auto mb-10 leading-relaxed">
                        Oops! The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            href="/"
                            className="group px-8 py-3 bg-primary text-white rounded-full font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
                        >
                            <Home className="w-5 h-5" />
                            <span>Back to Home</span>
                        </Link>

                        <Link
                            href="/contact"
                            className="group px-8 py-3 bg-white text-gray-700 border border-gray-200 rounded-full font-bold flex items-center gap-2 hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                            <span>Contact Support</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>

                {/* Bottom Stats/Help (Optional visual balance) */}
                <div
                    ref={statsRef}
                    className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto opacity-0"
                >
                    <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center gap-3 justify-center text-left hover:shadow-md transition-shadow">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-primary">
                            <Activity className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">System Status</p>
                            <p className="text-xs text-blue-600 font-medium">All systems operational</p>
                        </div>
                    </div>

                    <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center gap-3 justify-center text-left hover:shadow-md transition-shadow">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-primary">
                            <Search className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">Looking for services?</p>
                            <Link href="/services" className="text-xs text-blue-600 font-medium hover:underline">View all services</Link>
                        </div>
                    </div>

                    <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center gap-3 justify-center text-left hover:shadow-md transition-shadow">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-primary">
                            <ArrowRight className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">Need help?</p>
                            <Link href="/contact" className="text-xs text-blue-600 font-medium hover:underline">Get in touch</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
